import React, { createRef, useEffect, useState } from 'react';
import { useSelect } from 'downshift';
import { Box, Button, Text } from '@chakra-ui/react';
import { ChevronDown20 } from '@carbon/icons-react';
import { flatten, keys, pickBy } from 'ramda';
import { useClickAway } from 'react-use';

import { SelectData, OnChangeCallbackData, SelectOptions } from './types';
import SelectDropdown from './components/SelectDropdown';
import SelectContext, {
  defaultSelectionData,
  ROOT_PARENT_ID,
  SelectionData,
} from './Context';

interface SelectProps {
  data: SelectData;
  defaultSelectedData?: SelectionData;
  label?: string | number | (string | number)[];
  forceGroupSelections?: boolean;
  width?: number | string;
  onChange: (selectedData: OnChangeCallbackData[]) => void;
  defaultSelections?: SelectionData['selectedGroupIds'];
  dark?: boolean;
}

const ChevronIcon: React.FC<{ isOpen: boolean; dark: boolean }> = ({
  isOpen,
  dark,
}) => (
  <Box
    as={ChevronDown20}
    transition=".2s transform ease-out"
    transform={`rotate(${isOpen ? 180 : 0}deg)`}
    color={dark ? 'gray.100' : 'gray.700'}
    pointerEvents="none"
  />
);

const getDefaultSelections = (initData: SelectData) => {
  const selections: SelectionData['selectedGroupIds'] = {};

  const getNextValue = (parentId: string, data: SelectData) => {
    if (data.meta?.selected) {
      const { selected } = data.meta;

      selections[parentId] = {};

      if (Array.isArray(selected)) {
        selected.forEach((sel) => {
          selections[parentId][sel] = true;
        });
      } else {
        selections[parentId][selected] = true;
      }
    }

    data.options.forEach(
      (option) => option.children && getNextValue(option.key, option.children),
    );
  };

  getNextValue(ROOT_PARENT_ID, initData);

  return selections;
};

const Select = ({
  data,
  defaultSelectedData,
  forceGroupSelections = false,
  label = '',
  width = '100%',
  onChange,
  dark = false,
}: SelectProps) => {
  const {
    selectedItem,
    getLabelProps,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
  } = useSelect({
    items: data.options,
  });

  const defaultSelections = getDefaultSelections(data);
  const dropdownRef = createRef<HTMLDivElement>();
  const [selectionData, setSelectionData] = useState<SelectionData>(
    defaultSelectedData || {
      ...defaultSelectionData,
      selectedGroupIds:
        defaultSelections || defaultSelectionData.selectedGroupIds,
    },
  );
  const [selectedLabel, setSelectedLabel] = useState<
    string | number | (string | number)[] | null
  >(selectedItem?.label || label);

  const [isOpen, setIsOpen] = useState<boolean>(false);

  useClickAway(dropdownRef, () => isOpen && setIsOpen(false));

  useEffect(() => {
    const updatedSelections = getDefaultSelections(data);

    setSelectionData({
      ...selectionData,
      selectedGroupIds: updatedSelections,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    const groupIds = selectionData.selectedGroupIds;
    const selectedGroupKeys = keys(groupIds) as string[];
    const onChangeData = selectedGroupKeys
      .map((id: string) => {
        const selectedItems = keys(pickBy(Boolean, groupIds[id]));

        return (
          selectedItems.length && {
            groupId: id,
            selectedItems,
          }
        );
      })
      .filter(Boolean) as OnChangeCallbackData[];

    setSelectedLabel(
      onChangeData.length
        ? flatten(onChangeData.map(({ selectedItems }) => selectedItems))
        : null,
    );

    onChange(onChangeData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionData]);

  const renderLabel = (value: string | number | (string | number)[] | null) => {
    if (!value) {
      return label;
    }

    if (Array.isArray(value)) {
      const parentValuesSelected = value.reduce<boolean>(
        (acc, selectedItemKey) =>
          acc && !!data.options.find(({ key }) => key === selectedItemKey),
        true,
      );

      const findChildLabel = (options: SelectOptions[]): string => {
        const found = options.find(({ key }) => key === value[0]);

        if (!found) {
          return options.reduce<string>((acc, option) => {
            const childLabel = findChildLabel(option.children?.options || []);
            return childLabel === '' ? acc : childLabel;
          }, '');
        }

        return found.label;
      };

      if (!parentValuesSelected) {
        if (value.length === 1) {
          return findChildLabel(data.options);
        }

        return `${value.length} selected`;
      }

      if (value.length === 1) {
        return data.options.find(({ key }) => key === value[0])?.label;
      }
      return `${value.length} selected out of ${data.options.length}`;
    }
    return data.options.find(({ key }) => key === value)?.label || label;
  };

  return (
    <SelectContext.Provider value={{ selectionData, setSelectionData }}>
      <Box position="relative" ref={dropdownRef} w={width}>
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <Text
          as="label"
          {...getLabelProps()}
          display="none"
          data-testid="selectLabel"
        >
          {renderLabel(selectedLabel)}
        </Text>

        <Button
          {...getToggleButtonProps()}
          w={width}
          h="40px"
          variant="outline"
          rightIcon={() => <ChevronIcon isOpen={isOpen} dark={dark} />}
          justifyContent="space-between"
          fontWeight={400}
          color={dark ? 'white' : 'gray.700'}
          bg={dark ? 'gray.700' : 'white'}
          _hover={{ bg: dark ? 'gray.900' : 'white' }}
          _active={{ bg: dark ? 'gray.900' : 'white' }}
          borderColor={dark ? 'gray.900' : 'gray.200'}
          data-testid="selectTriggerButton"
          onClick={() => setIsOpen(!isOpen)}
          isDisabled={data.options.length === 0}
        >
          <Box
            overflow="hidden"
            whiteSpace="nowrap"
            textOverflow="ellipsis"
            maxWidth="100%"
          >
            {renderLabel(selectedLabel)}
          </Box>
        </Button>
        <Box {...getMenuProps()} w={width}>
          {isOpen && (
            <Box
              boxShadow="md"
              w={width}
              borderRadius="md"
              bg={dark ? 'gray.700' : 'white'}
              position="absolute"
              top="48px"
              border="1px solid"
              borderColor={dark ? 'gray.900' : 'gray.200'}
              zIndex="dropdown"
              pb={4}
              overflow="auto"
              maxH={200}
            >
              <SelectDropdown
                dark={dark}
                getMenuProps={getMenuProps}
                data={data.options}
                isOpen={isOpen}
                getItemProps={getItemProps}
                meta={data.meta}
                forceGroupSelections={forceGroupSelections}
              />
            </Box>
          )}
        </Box>
      </Box>
    </SelectContext.Provider>
  );
};

export default Select;
